import type {
    MoneyMapAccountWithAccountDetails,
    MoneyMapApexAccountDetails,
    MoneyMapRuleWithAccount,
    MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import {
    isRetirementRegistrationType,
    MoneyMapAccountType,
    MoneyMapCategory,
    MoneyMapRuleType,
    PaycheckRoutingRuleType,
} from '@fruitful/types';
import { keyBy, orderBy } from 'lodash';

import { getMoneyMapAccountSubtitle } from '../components/MoneyMapAccountRuleRedesign';
import type { RoutingRuleCardProps } from '../components/MoneyMapRoutingRuleCard';
import { getDisplayAccountsForMoneyMapRule } from './moneyMapAccountUtils';
import { MONTHLY_EXPENSES_SET_ID, UNGROUPED_GOALS_SET_ID } from './ruleSetIds';

export interface EditableRuleSet {
    id: string;
    name: string;
    priority: number;
    rules: Array<RoutingRuleCardProps & { ruleId: string }>;
}

const ruleHasStoppingCondition = (
    rule: RoutingRuleCardProps & { ruleId: string },
): boolean => {
    return (
        (rule.targetBalanceCents ?? 0) > 0 ||
        (rule.monthlyContributionMaxCents ?? 0) > 0 ||
        (rule.yearlyContributionMaxCents ?? 0) > 0 ||
        rule.hasRetirementContributionLimit === true
    );
};

/**
 * Id of the first rule set that would trap remaining income so later sets
 * never run. Zero-contribution rules are ignored; among the rest, any rule
 * without a target/cap (or min-balance) is treated as a stop.
 */
export const getFirstIncomeSplitStopRuleSetId = (
    ruleSets: EditableRuleSet[],
): string | null => {
    for (let i = 0; i < ruleSets.length - 1; i++) {
        const ruleSet = ruleSets[i];
        if (ruleSet == null) continue;

        // Card percentages are decimal 0–1 (goals + derived monthly-expense shares).
        const contributingRules = ruleSet.rules.filter(
            (rule) => (rule.percentage ?? 0) > 0,
        );
        if (contributingRules.length === 0) continue;

        if (contributingRules.some((rule) => !ruleHasStoppingCondition(rule))) {
            return ruleSet.id;
        }
    }

    return null;
};

const getRuleAmountCents = (rule: RoutingRuleCardProps): number => {
    // BILLS monthly amount is in targetBalanceCents; SPEND in monthlyContributionMaxCents.
    if (rule.routingRuleType === PaycheckRoutingRuleType.MINIMUM_BALANCE) {
        return rule.targetBalanceCents ?? 0;
    }
    if (rule.routingRuleType === PaycheckRoutingRuleType.PERCENTAGE) {
        return rule.monthlyContributionMaxCents ?? 0;
    }
    return rule.targetBalanceCents ?? rule.monthlyContributionMaxCents ?? 0;
};

export const computeEditableRuleSets = (
    moneyMap: MoneyMapWithRulesAndAccounts,
    accounts: MoneyMapAccountWithAccountDetails[],
): EditableRuleSet[] => {
    const accountsById = keyBy(accounts, 'id');
    const rules = moneyMap.moneyMapRules;
    const groups = moneyMap.moneyMapRuleGroups;

    const billRules = rules.filter((r) => r.type === MoneyMapRuleType.BILLS);
    const spendRules = rules.filter((r) => r.type === MoneyMapRuleType.SPEND);
    const goalRules = rules.filter((r) => r.type === MoneyMapRuleType.GOAL);

    const mapRuleToCard = (
        rule: MoneyMapRuleWithAccount,
        category: MoneyMapCategory,
    ): RoutingRuleCardProps & { ruleId: string } => {
        const { destinationAccount, primaryAccount } =
            getDisplayAccountsForMoneyMapRule(rule, accountsById);
        const hasRetirementContributionLimit =
            destinationAccount?.type ===
                MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT &&
            isRetirementRegistrationType(
                (
                    destinationAccount?.accountDetails as
                        | MoneyMapApexAccountDetails
                        | undefined
                )?.apexAccountType,
            );
        const isBackdoorTrad =
            destinationAccount?.type ===
                MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT &&
            destinationAccount.backdoorRothLinkedMoneyMapAccountId != null;
        return {
            ruleId: rule.id,
            accountName:
                primaryAccount?.accountDetails?.name ?? rule.description,
            accountSubtitle:
                primaryAccount != null
                    ? getMoneyMapAccountSubtitle(primaryAccount)
                    : undefined,
            description: rule.description,
            category,
            account:
                primaryAccount != null
                    ? { ...primaryAccount, hasRetirementContributionLimit }
                    : {},
            routingRuleType: rule.routingRuleType ?? null,
            percentage:
                rule.percentage != null ? Number(rule.percentage) : null,
            fixedAmountCents: rule.fixedAmountCents ?? null,
            monthlyContributionMaxCents:
                rule.monthlyContributionMaxCents ?? null,
            targetBalanceCents: rule.targetBalanceCents ?? null,
            yearlyContributionMaxCents: rule.yearlyContributionMaxCents ?? null,
            hasRetirementContributionLimit,
            isBackdoorTrad,
        };
    };

    const sets: EditableRuleSet[] = [];

    if (billRules.length > 0 || spendRules.length > 0) {
        const mappedBillRules = [...billRules].map((r) =>
            mapRuleToCard(
                r,
                r.type === MoneyMapRuleType.BILLS
                    ? MoneyMapCategory.BILLS
                    : MoneyMapCategory.SPEND,
            ),
        );

        const mappedSpendRules = [...spendRules].map((r) =>
            mapRuleToCard(
                r,
                r.type === MoneyMapRuleType.BILLS
                    ? MoneyMapCategory.BILLS
                    : MoneyMapCategory.SPEND,
            ),
        );

        const totalSetAmount = [...mappedBillRules, ...mappedSpendRules].reduce(
            (sum, rule) => sum + getRuleAmountCents(rule),
            0,
        );

        const billRulesWithDerivedPct = orderBy(
            mappedBillRules.map((rule) => ({
                ...rule,
                percentage:
                    totalSetAmount > 0
                        ? getRuleAmountCents(rule) / totalSetAmount
                        : null,
            })),
            'percentage',
            'desc',
        );

        const spendRulesWithDerivedPct = orderBy(
            mappedSpendRules.map((rule) => ({
                ...rule,
                percentage:
                    totalSetAmount > 0
                        ? getRuleAmountCents(rule) / totalSetAmount
                        : null,
            })),
            'percentage',
            'desc',
        );

        sets.push({
            id: MONTHLY_EXPENSES_SET_ID,
            name: 'Monthly expenses',
            priority: 0,
            rules: [...billRulesWithDerivedPct, ...spendRulesWithDerivedPct],
        });
    }

    const goalsByGroup = new Map<string | null, MoneyMapRuleWithAccount[]>();
    const sortedGoalRules = orderBy(goalRules, 'percentage', 'desc');
    for (const rule of sortedGoalRules) {
        const groupId = rule.moneyMapRuleGroupId ?? null;
        const existing = goalsByGroup.get(groupId) ?? [];
        existing.push(rule);
        goalsByGroup.set(groupId, existing);
    }

    const knownGroupIds = new Set(groups.map((g) => g.id));

    // Goal rules whose group isn't among the map's rule groups (legacy
    // ungrouped goals, or a group that was deleted out from under its rules).
    for (const [groupId, groupRules] of goalsByGroup) {
        if (groupId != null && knownGroupIds.has(groupId)) continue;
        sets.push({
            id: groupId ?? UNGROUPED_GOALS_SET_ID,
            name: 'Goals',
            priority: 1,
            rules: orderBy(groupRules, ['priority']).map((r) =>
                mapRuleToCard(r, MoneyMapCategory.GOALS),
            ),
        });
    }

    // Every rule group gets a set, even ones with no goals yet, so a newly
    // created (empty) goal set still shows up in the list.
    for (const group of groups) {
        const groupRules = goalsByGroup.get(group.id) ?? [];
        sets.push({
            id: group.id,
            name: group.name,
            priority: group.priority + 1,
            rules: orderBy(groupRules, ['priority']).map((r) =>
                mapRuleToCard(r, MoneyMapCategory.GOALS),
            ),
        });
    }

    return orderBy(sets, ['priority']);
};
